import { dispatch } from 'lib/store'
import apiClient from 'lib/api-client'
import {
  SET_LOGS_DATA,
  APPEND_LOGS_DATA,
  SET_LOGS_NEXT_TOKEN_DATA,
  SET_FETCHING_LOGS_DATA,
  SET_FETCHING_MORE_LOGS_DATA,
  CLEAR_LOGS_DATA,
} from 'constants/action-types'
import { getCommonQueryFilters, isDemoLoading } from 'selectors/search/filters'
import { getLogsNextToken, getLogs } from 'selectors/search/logs'

const LOGS_QUERY = `
  query Logs($filter: LogsFilter, $nextToken: String) {
    logs(filter: $filter, nextToken: $nextToken) {
      items {
        id
        dateTime
        data
        meta
      }
      nextToken
    }
  }
`

interface LogResponse {
  id: string
  dateTime: string
  data: Record<string, unknown>
}

interface LogsQueryResponse {
  data: {
    logs: {
      items: LogResponse[]
      nextToken: string | null
    }
  }
}

interface LogsFilter {
  dateRange?: {
    from?: string | null
    to?: string | null
  }
  attributeValues?: Array<{
    attributeCode: string
    attributeValue: string
  }>
}

/**
 * Fetch logs from GraphQL API
 * Uses common query filters from selector (memoized)
 * Errors are caught by global error handler
 *
 * @param nextToken - Optional pagination token. If null/undefined = initial load, if string = load more
 */
export const fetchLogs = async (nextToken?: string | null): Promise<void> => {
  const isInitialLoad = !nextToken

  dispatch(
    isInitialLoad ? SET_FETCHING_LOGS_DATA : SET_FETCHING_MORE_LOGS_DATA,
    true,
  )

  try {
    // Get filters from selector (memoized, consistent across all queries)
    const commonFilters = getCommonQueryFilters()

    // Build filter with spread (includes all common filters + any future additions)
    const filter: LogsFilter = {
      ...commonFilters,
    }

    const response = await apiClient.post<LogsQueryResponse>('/graphql', {
      query: LOGS_QUERY,
      variables: {
        filter: Object.keys(filter).length > 0 ? filter : undefined,
        nextToken: nextToken || null,
      },
    })

    const { items, nextToken: newNextToken } = response.data.data.logs

    // Initial load replaces data, pagination appends data
    dispatch(isInitialLoad ? SET_LOGS_DATA : APPEND_LOGS_DATA, items)
    dispatch(SET_LOGS_NEXT_TOKEN_DATA, newNextToken)
  } finally {
    // In demo mode, keep loader active on initial load if no data - covers both
    // empty API response and error cases (retry will follow)
    if (!(isDemoLoading() && isInitialLoad && getLogs().length === 0)) {
      dispatch(
        isInitialLoad ? SET_FETCHING_LOGS_DATA : SET_FETCHING_MORE_LOGS_DATA,
        false,
      )
    }
  }
}

/**
 * Fetch more logs (pagination)
 * Convenience wrapper that reads nextToken from selector and calls fetchLogs
 */
export const fetchMoreLogs = async (): Promise<void> => {
  const nextToken = getLogsNextToken()

  // If no nextToken, nothing to fetch
  if (!nextToken) {
    return
  }

  await fetchLogs(nextToken)
}

/**
 * Clear logs data
 */
export const clearLogs = (): void => {
  dispatch(CLEAR_LOGS_DATA, undefined)
}
